import axios from "axios";
import jwt_decode from "jwt-decode";
import dotenv from "dotenv";
import { setAuthPermission } from "helpers/checkForbidden";
import { setRecoilAuth, getRecoilAuth } from "helpers/recoilAuth";
dotenv.config();

const instance = axios.create({
  timeout: 30000,
  baseURL: process.env.REACT_APP_API_URL,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => {
    if (error) {
      p.reject(error);
    } else {
      p.resolve(token);
    }
  });
  failedQueue = [];
};

// Refresh when elapsed > 80% of token lifetime
function isTokenExpiredOrExpiring() {
  const auth = getRecoilAuth();
  if (!auth?.exp || !auth?.iat) return false;
  const lifetime = auth.exp - auth.iat;
  const nowSec = Math.floor(Date.now() / 1000);
  return nowSec >= auth.iat + lifetime * 0.8;
}

function doRefresh() {
  return instance({
    url: "usermanagement/v1/auth/refreshtoken",
    method: "POST",
    withCredentials: true,
    _skipAuthRefresh: true,
  }).then((res) => {
    const accessToken = res.data.data.access_token;
    const decoded = jwt_decode(accessToken);
    setAuthPermission(decoded.permission);
    setRecoilAuth({ ...decoded, access_token: accessToken });
    return accessToken;
  });
}

// Request interceptor — proactively refresh before sending if token is expiring
instance.interceptors.request.use(
  (config) => {
    if (!config.headers["Accept-Language"]) {
      config.headers["Accept-Language"] = "en";
    }

    if (config._skipAuthRefresh || !isTokenExpiredOrExpiring()) {
      return config;
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          config.headers["Authorization"] = `Bearer ${token}`;
          return config;
        })
        .catch((err) => Promise.reject(err));
    }

    isRefreshing = true;
    return doRefresh()
      .then((token) => {
        processQueue(null, token);
        config.headers["Authorization"] = `Bearer ${token}`;
        return config;
      })
      .catch((err) => {
        processQueue(err);
        window.location.hash = "#/auth/authentication/sign-in";
        return Promise.reject(err);
      })
      .finally(() => {
        isRefreshing = false;
      });
  },
  (error) => Promise.reject(error)
);

// Response interceptor — fallback for unexpected 401
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest._skipAuthRefresh
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return instance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return doRefresh()
        .then((token) => {
          processQueue(null, token);
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return instance(originalRequest);
        })
        .catch((err) => {
          processQueue(err);
          window.location.hash = "#/auth/authentication/sign-in";
          return Promise.reject(err);
        })
        .finally(() => {
          isRefreshing = false;
        });
    }

    return Promise.reject(error);
  }
);

export default instance;
